/**
 * DepartmentTrendChart
 * Line chart showing performance trends over time
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  useTheme,
} from '@mui/material';
import { TrendingUp, Activity, Target, Timer } from 'lucide-react';

import { DepartmentPerformance, PerformancePeriod } from '@/types';

interface DepartmentTrendChartProps {
  performance: DepartmentPerformance | undefined;
  period: PerformancePeriod;
  isLoading: boolean;
}

type MetricType = 'completion' | 'velocity' | 'utilization' | 'onTime';

interface TrendPoint {
  label: string;
  value: number;
}

const DepartmentTrendChart: React.FC<DepartmentTrendChartProps> = ({
  performance,
  period,
  isLoading,
}) => {
  const theme = useTheme();
  const [activeMetric, setActiveMetric] = useState<MetricType>('completion');

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width={200} />
          <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  if (!performance) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">No trend data available</Typography>
        </CardContent>
      </Card>
    );
  }

  const metricConfig: Record<MetricType, { label: string; icon: React.ReactNode; color: string; unit: string }> = {
    completion: {
      label: 'Completion Rate',
      icon: <Target size={16} />,
      color: theme.palette.primary.main,
      unit: '%',
    },
    velocity: {
      label: 'Velocity',
      icon: <Activity size={16} />,
      color: theme.palette.success.main,
      unit: ' tasks/week',
    },
    utilization: {
      label: 'Utilization',
      icon: <Timer size={16} />,
      color: theme.palette.warning.main,
      unit: '%',
    },
    onTime: {
      label: 'On-Time Delivery',
      icon: <TrendingUp size={16} />,
      color: theme.palette.info.main,
      unit: '%',
    },
  };

  // Generate mock trend data based on period
  const generateTrendData = (metric: MetricType): TrendPoint[] => {
    const baseValues: Record<MetricType, number> = {
      completion: performance.completionRate,
      velocity: performance.avgVelocity,
      utilization: performance.avgUtilization,
      onTime: performance.onTimeDeliveryRate,
    };

    const base = baseValues[metric];
    const points: TrendPoint[] = [];
    const variance = 10;

    let periodCount: number;
    let labelFormat: (i: number) => string;

    switch (period) {
      case 'day':
        periodCount = 7;
        labelFormat = (i) => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i];
        break;
      case 'week':
        periodCount = 8;
        labelFormat = (i) => `Week ${i + 1}`;
        break;
      case 'month':
        periodCount = 6;
        labelFormat = (i) => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'][i];
        break;
      case 'quarter':
        periodCount = 4;
        labelFormat = (i) => `Q${i + 1}`;
        break;
      case 'year':
        periodCount = 5;
        labelFormat = (i) => `${2020 + i}`;
        break;
      default:
        periodCount = 7;
        labelFormat = (i) => `Day ${i + 1}`;
    }

    for (let i = 0; i < periodCount; i++) {
      // Generate slightly increasing trend
      const trendFactor = (i / (periodCount - 1)) * 5;
      const randomVariation = (Math.random() - 0.5) * variance;
      let value = base - variance / 2 + trendFactor + randomVariation;
      
      // Keep within bounds
      if (metric === 'velocity') {
        value = Math.max(1, Math.min(50, value));
      } else {
        value = Math.max(0, Math.min(100, value));
      }
      
      points.push({
        label: labelFormat(i),
        value: Math.round(value * 10) / 10,
      });
    }

    return points;
  };

  const trendData = generateTrendData(activeMetric);
  const config = metricConfig[activeMetric];

  // Calculate chart dimensions
  const chartWidth = 600;
  const chartHeight = 250;
  const padding = { top: 20, right: 40, bottom: 40, left: 50 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Calculate scales
  const maxValue = Math.max(...trendData.map(d => d.value)) * 1.1;
  const minValue = Math.min(...trendData.map(d => d.value)) * 0.9;
  const valueRange = maxValue - minValue;

  const xScale = (index: number) => padding.left + (index / (trendData.length - 1)) * innerWidth;
  const yScale = (value: number) => padding.top + innerHeight - ((value - minValue) / valueRange) * innerHeight;

  // Generate path for the line
  const linePath = trendData
    .map((point, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(point.value)}`)
    .join(' ');

  // Generate path for the area
  const areaPath = `
    ${linePath}
    L ${xScale(trendData.length - 1)} ${yScale(minValue)}
    L ${xScale(0)} ${yScale(minValue)}
    Z
  `;

  // Calculate average and trend
  const avg = trendData.reduce((sum, p) => sum + p.value, 0) / trendData.length;
  const firstHalfAvg = trendData.slice(0, Math.floor(trendData.length / 2)).reduce((sum, p) => sum + p.value, 0) / Math.floor(trendData.length / 2);
  const secondHalfAvg = trendData.slice(Math.floor(trendData.length / 2)).reduce((sum, p) => sum + p.value, 0) / (trendData.length - Math.floor(trendData.length / 2));
  const trendDirection = secondHalfAvg > firstHalfAvg ? 'up' : 'down';
  const trendPercent = Math.abs(((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100).toFixed(1);

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp size={20} style={{ color: '#64748b' }} />
            <Typography variant="h6" fontWeight={600}>
              Performance Trends
            </Typography>
          </Box>
          
          <ToggleButtonGroup
            value={activeMetric}
            exclusive
            onChange={(_, value) => value && setActiveMetric(value)}
            size="small"
          >
            <ToggleButton value="completion" sx={{ textTransform: 'none', px: 2 }}>
              <Target size={14} style={{ marginRight: 6 }} />
              Completion
            </ToggleButton>
            <ToggleButton value="velocity" sx={{ textTransform: 'none', px: 2 }}>
              <Activity size={14} style={{ marginRight: 6 }} />
              Velocity
            </ToggleButton>
            <ToggleButton value="utilization" sx={{ textTransform: 'none', px: 2 }}>
              <Timer size={14} style={{ marginRight: 6 }} />
              Utilization
            </ToggleButton>
            <ToggleButton value="onTime" sx={{ textTransform: 'none', px: 2 }}>
              <TrendingUp size={14} style={{ marginRight: 6 }} />
              On-Time
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Stats Row */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: config.color,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {config.label}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Average: <strong>{avg.toFixed(1)}{config.unit}</strong>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Trend:
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: trendDirection === 'up' ? theme.palette.success.main : theme.palette.error.main }}
            >
              {trendDirection === 'up' ? '↑' : '↓'} {trendPercent}%
            </Typography>
          </Box>
        </Box>

        {/* Chart */}
        <Box sx={{ display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
          <svg width="100%" viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ maxWidth: chartWidth }}>
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((fraction, i) => {
              const y = padding.top + innerHeight * (1 - fraction);
              const value = minValue + valueRange * fraction;
              return (
                <g key={i}>
                  <line
                    x1={padding.left}
                    y1={y}
                    x2={chartWidth - padding.right}
                    y2={y}
                    stroke="#e5e7eb"
                    strokeDasharray="4,4"
                  />
                  <text
                    x={padding.left - 8}
                    y={y + 4}
                    textAnchor="end"
                    fontSize="11"
                    fill="#64748b"
                  >
                    {value.toFixed(0)}
                  </text>
                </g>
              );
            })}

            {/* Area fill */}
            <path
              d={areaPath}
              fill={alpha(config.color, 0.1)}
            />

            {/* Line */}
            <path
              d={linePath}
              fill="none"
              stroke={config.color}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Data points */}
            {trendData.map((point, i) => (
              <g key={i}>
                {/* Vertical guideline on hover (static for now) */}
                <circle
                  cx={xScale(i)}
                  cy={yScale(point.value)}
                  r="5"
                  fill="white"
                  stroke={config.color}
                  strokeWidth="2"
                />
                
                {/* X-axis labels */}
                <text
                  x={xScale(i)}
                  y={chartHeight - padding.bottom + 20}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#64748b"
                >
                  {point.label}
                </text>

                {/* Value labels on points */}
                <text
                  x={xScale(i)}
                  y={yScale(point.value) - 12}
                  textAnchor="middle"
                  fontSize="10"
                  fill={config.color}
                  fontWeight="600"
                >
                  {point.value.toFixed(activeMetric === 'velocity' ? 0 : 1)}
                </text>
              </g>
            ))}

            {/* Average line */}
            <line
              x1={padding.left}
              y1={yScale(avg)}
              x2={chartWidth - padding.right}
              y2={yScale(avg)}
              stroke={config.color}
              strokeWidth="1"
              strokeDasharray="6,4"
              opacity={0.5}
            />
          </svg>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 3,
                bgcolor: config.color,
                borderRadius: 1,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {config.label}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 1,
                bgcolor: config.color,
                opacity: 0.5,
                borderRadius: 1,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Average ({avg.toFixed(1)})
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DepartmentTrendChart;
